import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useQuery } from '@apollo/client';
import { ATTRIBUTES, FIELDS_LIST } from '@modules/booking/customerForm/queries';

import { useBookingContext } from '@common/context/BookingContext';
import { AddressDetails } from '@common/fields/AddressField/googleMaps';
import useFeatureFlag from '@common/utils/hooks/useFeatureFlag';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import {
  AttributesQuery,
  DataType,
  FieldsListQuery,
  ListingsQuery,
  MeetingTypesQuery,
} from '@typings/api';

import { MainFormFields } from '../BookingFlow';
import { LISTINGS, MEETING_TYPES } from '../queries';

interface IAddressContext {
  address?: AddressDetails;
  addresses: Record<string, AddressDetails & { label: string }>;
  addressKey?: string;
  onChangeAddressKey: (string: string) => void;
  onChangeAddress: (address?: AddressDetails) => void;
}

export const AddressContext = createContext<IAddressContext>({
  addresses: {},
  onChangeAddressKey: () => {
    throw new Error('Did you use useAddress() outside of the AddressProvider?');
  },
  onChangeAddress: () => {
    throw new Error('Did you use useAddress() outside of the AddressProvider?');
  },
});

interface AddressProviderProps {
  children: ReactNode;
}

export const LISTING_ADDRESS_KEY = 'listing.location';
export const OTHER_ADDRESS_KEY = 'other';

export function AddressProvider(props: AddressProviderProps) {
  const { id: contextId } = useQueryParams();
  const [{ categoryId }] = useBookingContext();
  const isListingsEnabled = useFeatureFlag('steps.listings.enabled');

  const { change } = useForm();
  const {
    values: {
      address,
      addressKey,
      leadSegmentId,
      listingId,
      meetingType,
      subjectId,
    },
  } = useFormState();

  const { data: meetingTypesData } = useQuery<MeetingTypesQuery>(
    MEETING_TYPES,
    {
      variables: {
        subjectId,
        leadSegmentId,
        listingId,
      },
      skip: !subjectId || !leadSegmentId,
    },
  );

  const defaultAddressKey = useMemo(
    () =>
      meetingTypesData?.meetingTypes?.find((m) => m.label === meetingType)
        ?.defaultAddressKey,
    [meetingTypesData, meetingType],
  );

  const { data: fieldsData } = useQuery<FieldsListQuery>(FIELDS_LIST, {
    variables: { categoryId, active: true },
    skip: !categoryId,
  });
  const { data: attributesData } = useQuery<AttributesQuery>(ATTRIBUTES, {
    variables: { contextId },
    notifyOnNetworkStatusChange: true,
    skip: !contextId,
  });
  const { data: listingsData } = useQuery<ListingsQuery>(LISTINGS, {
    variables: {
      contextId,
    },
    skip: !isListingsEnabled || !contextId || !listingId,
  });
  const listing = useMemo(
    () =>
      (listingsData?.listings ?? []).find(
        (listing) => listing.id === listingId,
      ),
    [listingsData, listingId],
  );

  const addressLookup: Record<string, AddressDetails & { label: string }> =
    useMemo(() => {
      const categoryFields = fieldsData?.category?.fields ?? [];
      const contextAddressses = (
        attributesData?.context?.attributes ?? []
      ).filter((attribute) => attribute.field.dataType === DataType.ADDRESS);

      const lookup = contextAddressses.reduce((lookup, address) => {
        const hasMatchingAddress = categoryFields.some(
          (field) => field.key === address.field.key,
        );

        if (hasMatchingAddress && address.value) {
          const label =
            categoryFields.find((field) => field.key === address.field.key)
              ?.label ?? '';

          return {
            ...lookup,
            [address.field.key]: {
              label,
              ...JSON.parse(address.value),
            },
          };
        }

        return lookup;
      }, {});

      return listing?.location
        ? {
            ...lookup,
            [LISTING_ADDRESS_KEY]: {
              label: 'Listing Location', // TODO: translate
              ...listing.location,
            },
          }
        : lookup;
    }, [fieldsData, attributesData, listing]);

  const handleAddressChange = useCallback(
    (address?: AddressDetails) => {
      if (address !== undefined) {
        return change(MainFormFields.ADDRESS, {
          ...address,
          location: address.location
            ? {
                lat: address.location.lat,
                lng: address.location.lng,
              }
            : undefined,
        });
      }

      return change(MainFormFields.ADDRESS, undefined);
    },
    [change],
  );

  const handleAddressKeyChange = useCallback(
    (key: string) => {
      const newAddress = addressLookup[key];

      change(MainFormFields.ADDRESS_KEY, key);
      handleAddressChange(newAddress);
    },
    [addressLookup, change, handleAddressChange],
  );

  useEffect(() => {
    if (addressLookup[LISTING_ADDRESS_KEY]) {
      return handleAddressKeyChange(LISTING_ADDRESS_KEY);
    }

    if (defaultAddressKey && addressLookup[defaultAddressKey]) {
      return handleAddressKeyChange(defaultAddressKey);
    }

    const addressKeys = Object.keys(addressLookup);

    if (addressKeys.length > 0) {
      return handleAddressKeyChange(addressKeys[0]);
    }

    return handleAddressKeyChange(OTHER_ADDRESS_KEY);
  }, [addressLookup, defaultAddressKey, handleAddressKeyChange]);

  return (
    <AddressContext.Provider
      {...props}
      value={{
        address,
        addressKey,
        addresses: addressLookup,
        onChangeAddress: handleAddressChange,
        onChangeAddressKey: handleAddressKeyChange,
      }}
    />
  );
}

export function useAddress() {
  return useContext(AddressContext);
}
